import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import glamorous, { withTheme } from 'glamorous';
import { Card, CardText, CardActions } from 'react-toolbox/lib/card';
import LoadingView from '../../../../../common/components/LoadingView';
import Redirect from '../../../../../common/components/Redirect';
import {
  TitleBar,
  DocTitle,
} from '../../../../../common/components/Layout/StandardPageTemplate/Context';
import withQuery from '../../../../../common/components/withQuery';
import composes from '../../../../../common/utils/composes';
import { withLoading } from '../../../../../redux-modules/loading-overlay';
import CurrentUserActions from '../../../../../redux-modules/current-user';
import SurveyActions from '../../../actions/surveys';
import { identitiesApi } from '../../../actions/identities';
import SurveyResponseActions from '../../../actions/surveys/surveyResponses';
import { getSurvey } from '../../../selectors/surveys';
import ActionHandler from '../../ActionHandler';
import List from './ListLayout';
import Wizard from './WizardLayout';

const SurveyLayout = {
  List,
  Wizard,
};

const isAnAnswerThatCanHaveMultipleValues = Array.isArray;

export const handleAnswer = (answerValue, questionKey) => {
  if (isAnAnswerThatCanHaveMultipleValues(answerValue)) {
    return handleMultiAnswer(answerValue, questionKey);
  }

  if (typeof answerValue === 'string') {
    return [
      {
        answerValue: answerValue.trim(),
        questionKey,
      },
    ];
  }

  return [];
};

const handleMultiAnswer = (answerValue, questionKey) => {
  return answerValue.reduce((acc, curr) => {
    const answerObj = handleAnswer(curr, questionKey);
    return [...acc, ...answerObj];
  }, []);
};

const DetailComponent = props => {
  const { survey: { layout } } = props;
  const Component = SurveyLayout[layout] || SurveyLayout.List;
  return <Component {...props} />;
};

const MessageCard = ({ title, bodyText, children }) => {
  return (
    <Card style={{ padding: '3rem 3rem' }}>
      <CardText style={{ textAlign: 'center' }}>
        <h5
          style={{
            fontSize: '2.4rem',
            fontWeight: 400,
            marginBottom: '0.6rem',
          }}
        >
          {typeof title === 'function' ? title() : title}
        </h5>
        <glamorous.P color="#757575">
          {typeof bodyText === 'function' ? bodyText() : bodyText}
        </glamorous.P>
      </CardText>
      {children ? <CardActions>{children}</CardActions> : null}
    </Card>
  );
};

class DetailPage extends React.Component {
  state = {
    responses: {},
    errors: {},
    save: false,
    isLoggedIn: false,
    softAuthToken: null,
    softAuthCleared: false,
  };
  static contextTypes = {};
  static promises = {
    includePromises: [
      {
        meta: { namespace: 'surveys' },
      },
      {
        meta: { namespace: 'surveyResponses' },
      },
      {
        meta: { namespace: 'customers' },
      },
    ],
  };

  async componentDidMount() {
    if (
      this.props.currentUser.token &&
      this.props.currentUser.token.trim().length
    ) {
      this.setState({ isLoggedIn: true });
    } else {
      const { resp, json } = await identitiesApi
        .post(this.props.location.query, {
          params: { SecurityRole: 'SurveyRole' },
        })
      if (resp.ok) {
        await this.props.actions.currentUser.setId(json.key);
        await this.setState({ softAuthToken: resp.headers.get('authorizationtoken') });
        this.signInIfSurveyUser();
        this.setState({ isLoggedIn: true });
      }
    }
  }

  componentDidUpdate(prevProps) {
    const { survey = {}, currentCustomer, currentOrganization } = this.props;
    const { softAuthToken, softAuthCleared } = this.state;
    //if we're using soft auth, and all data is gathered, clear the auth in case they click any links
    if (
      softAuthToken &&
      survey.key &&
      currentCustomer &&
      currentOrganization &&
      !softAuthCleared
    ) {
      this.signOutIfSurveyUser();
      this.setState({ softAuthCleared: true })
    }
  }

  signInIfSurveyUser() {
    if (this.state.softAuthToken) {
      this.props.actions.currentUser.updateAuth(this.state.softAuthToken);
    }
  }

  signOutIfSurveyUser() {
    if (this.state.softAuthToken) {
      this.props.actions.currentUser.clearAuth();
    }
  }

  saveQuestionValue = questionId => (
    value,
    { isValid = true, message = '', restrictInput } = {}
  ) => {
    this.setState(prevState => ({
      responses: {
        ...prevState.responses,
        [questionId]:
          restrictInput && !isValid ? prevState.responses[questionId] : value,
      },
      errors: {
        ...prevState.errors,
        [questionId]: isValid || restrictInput ? '' : message,
      },
    }));
  };

  onQuestionBlur = questionId => (
    value,
    { isValid = true, message = '' } = {}
  ) => {
    if (!isValid) {
      this.setState(prevState => ({
        errors: {
          ...prevState.errors,
          [questionId]: message,
        },
      }));
    }
  };

  submitSurvey = () => {
    this.setState({ save: false });
    const { survey, currentCustomer = {}, actions, location: { query = {} } } = this.props;
    const { responses } = this.state;
    const reqBody = {
      individualKey: currentCustomer.key || query.individualKey,
      surveyKey: survey.key,
      surveyResponseAnswers: Object.keys(responses).reduce((acc, curr) => {
        const answer = handleAnswer(responses[curr], curr);
        return [...acc, ...answer];
      }, []),
    };

    //Make sure survey response is not duplicate. As per Bug 17481, Ballot Responses is duplicating in Netforum
    reqBody.surveyResponseAnswers = reqBody.surveyResponseAnswers.filter((value, index, self) =>
      index === self.findIndex((t) => (
        t.questionKey === value.questionKey
      ))
    );
    this.signInIfSurveyUser();
    const result =  actions.surveyResponses.post(reqBody);
    this.signOutIfSurveyUser();
    return result;
  };

  triggerSubmit = () => {
    this.setState({ save: true });
  };

  render() {
    const { match, actions, isLoading, currentCustomer } = this.props;
    const { responses, save, isLoggedIn, errors } = this.state;
    const surveyId = ((match || {}).params || {}).id;

    return (
      <div>
        <TitleBar title="My Ballots & Surveys" />
        <DocTitle title="My Ballots & Surveys" />
        <ActionHandler
          condition={({ isExecuting, results }) =>
            !!surveyId && !isExecuting && !results && isLoggedIn && currentCustomer}
          action={actions.surveys.get}
          data={[surveyId]}
        >
          {results => {
            if (results && !results.hasError && results.payload) {
              const { survey } = this.props;

              // must use survey from selector, wait for results to be populated into state
              if (!survey) {
                return <LoadingView />;
              }

              return (
                <ActionHandler
                  condition={save && !isLoading}
                  action={this.submitSurvey}
                >
                  {results => {
                    if (results && !results.hasError) {
                      // successfully submitted survey
                      const { match: { url = '' } = {} } = this.props;
                      return (
                        <Redirect
                          to={{
                            pathname: `${url.replace(/\/+$/, '')}/thank-you`,
                          }}
                        />
                      );
                    }

                    if (!survey.isAvailable) {
                      return (
                        <MessageCard
                          title={`${survey.type} Not Available`}
                          bodyText={`Thank you for choosing to make your voice heard. Unfortunately, this ${survey.type.toLowerCase()} is no longer available. If you have any questions, please contact NFIB Customer Care at 1-800-NFIB-NOW (1-800-634-2669).`}
                        />
                      );
                    }

                    if (survey.isAnswered) {
                      return (
                        <MessageCard
                          title="Previously Answered"
                          bodyText={`Thank you for choosing to make your voice heard. Our records show that you have already submitted your responses to the selected ${survey.type.toLowerCase()}. If you have any questions, please contact NFIB Customer Care at 1-800-NFIB-NOW (1-800-634-2669).`}
                        />
                      );
                    }

                    const hasResponses = Object.keys(responses).some(key => {
                      let response = responses[key] || '';
                      if (typeof response === 'string') {
                        response = response.trim();
                      }
                      return !!response.length;
                    });

                    const hasErrors = Object.keys(errors).some(
                      key => !!(errors[key] || '').trim().length
                    );

                    return (
                      <DetailComponent
                        survey={survey}
                        responses={responses}
                        errors={errors}
                        saveQuestionValue={this.saveQuestionValue}
                        onQuestionBlur={this.onQuestionBlur}
                        submitSurvey={this.triggerSubmit}
                        disableSubmit={!hasResponses || hasErrors}
                      />
                    );
                  }}
                </ActionHandler>
              );
            }
            return <LoadingView />;
          }}
        </ActionHandler>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { match } = ownProps;
  const id = ((match || {}).params || {}).id;
  return {
    survey: getSurvey(state, { id }),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    actions: {
      currentUser: bindActionCreators(CurrentUserActions, dispatch),
      surveys: bindActionCreators(SurveyActions, dispatch),
      surveyResponses: bindActionCreators(SurveyResponseActions, dispatch),
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(
  composes(DetailPage, withLoading, withQuery, withTheme)
);



// WEBPACK FOOTER //
// ./src/App/account/components/Surveys/DetailPage/index.js